// Theme configuration for the application based on the Figma design

// Color palette - Light Mode
export const colors = {
  // Primary colors
  primary: '#030213', // very dark navy/black
  primaryLight: '#1a1a2e',
  primaryDark: '#000000',
  primaryForeground: '#FFFFFF', // white
  primaryRgb: '3, 2, 19', // RGB values for primary color
  
  // Secondary colors
  secondary: '#f1f2f6', // very light blue-gray (oklch(0.95 0.0058 264.53))
  secondaryLight: '#f8f9fa',
  secondaryDark: '#e9ecef',
  
  // Status colors
  success: '#4CAF50',
  warning: '#FF9800',
  error: '#d4183d', // destructive red
  info: '#2196F3',
  
  // Vehicle status colors
  statusAvailable: '#4CAF50', // Success green
  statusWorking: '#2196F3',   // Blue
  statusInWorkshop: '#FF9800', // Warning orange
  statusOutOfService: '#d4183d', // Error red
  
  // Driver status colors
  statusActive: '#4CAF50',     // Success green
  statusInactive: '#9E9E9E',   // Gray
  statusVacation: '#FF9800',   // Warning orange
  statusSuspended: '#d4183d',  // Error red
  
  // Neutral colors
  white: '#FFFFFF',
  black: '#25272b', // very dark gray/black (oklch(0.145 0 0))
  gray50: '#f3f3f5', // input-background
  gray100: '#ececf0', // muted
  gray200: '#e9ebef', // accent
  gray300: '#cbced4', // switch-background
  gray400: '#717182', // muted-foreground
  gray500: '#555568',
  gray600: '#3A3A48',
  gray700: '#2A2A35',
  gray800: '#1A1A22',
  gray900: '#0A0A0F',
  
  // Additional colors for better chart visualization
  blue600: '#2563eb',
  green600: '#059669',
  yellow600: '#d97706',
  red600: '#dc2626',
  purple600: '#9333ea',
  indigo600: '#4338ca',
  pink600: '#db2777',
  orange600: '#ea580c',
  
  // Background
  background: '#FFFFFF', // white
  surface: '#FFFFFF',
  card: '#FFFFFF', // white
  sidebar: '#F9F9FB',
  foreground: '#25272b', // very dark gray/black
  
  // Text
  textPrimary: '#25272b', // foreground
  textPrimaryRgb: '37, 39, 43', // RGB values for text primary
  textSecondary: '#555568',
  textTertiary: '#717182', // muted-foreground
  textInverse: '#FFFFFF',
  
  // Border
  border: 'rgba(0, 0, 0, 0.1)', // 10% black transparency
  input: 'transparent',
  inputBackground: '#f3f3f5', // light gray
  
  // Accent & Muted
  muted: '#ececf0', // light gray
  mutedForeground: '#717182', // medium gray
  accent: '#e9ebef', // light blue-gray
  destructive: '#d4183d', // red
  
  // Chart colors (converted from oklch)
  chart1: '#d67e22', // oklch(0.646 0.222 41.116) - orange-ish
  chart2: '#3498db', // oklch(0.6 0.118 184.704) - blue-ish  
  chart3: '#2c3e50', // oklch(0.398 0.07 227.392) - dark blue
  chart4: '#f1c40f', // oklch(0.828 0.189 84.429) - yellow-green
  chart5: '#27ae60', // oklch(0.769 0.188 70.08) - green
};

// Typography based on Figma design
export const typography = {
  // Font sizes
  sizes: {
    xs: 12,
    sm: 14,
    base: 14, // Base font size from Figma (--font-size: 14px)
    md: 16,
    lg: 18,
    xl: 20,
    xxl: 24,
    xxxl: 30,
  },
  
  // Font weights (from Figma)
  weights: {
    normal: '400', // --font-weight-normal: 400
    medium: '500', // --font-weight-medium: 500
    semibold: '600',
    bold: '700',
  },
  
  // Line heights
  lineHeights: {
    tight: 1.2,
    normal: 1.5, // Most elements in Figma use line-height: 1.5
    loose: 1.8,
  },
  
  // Font styles for different text types (matching Figma definitions)
  h1: {
    fontSize: 24, // --text-2xl
    fontWeight: '500', // --font-weight-medium
    lineHeight: 1.5,
  },
  h2: {
    fontSize: 20, // --text-xl
    fontWeight: '500', // --font-weight-medium
    lineHeight: 1.5,
  },
  h3: {
    fontSize: 18, // --text-lg
    fontWeight: '500', // --font-weight-medium
    lineHeight: 1.5,
  },
  h4: {
    fontSize: 16, // --text-base
    fontWeight: '500', // --font-weight-medium
    lineHeight: 1.5,
  },
  body: {
    fontSize: 14, // --text-base (base font size)
    fontWeight: '400', // --font-weight-normal
    lineHeight: 1.5,
  },
  label: {
    fontSize: 14, // --text-base
    fontWeight: '500', // --font-weight-medium
    lineHeight: 1.5,
  },
  button: {
    fontSize: 14, // --text-base
    fontWeight: '500', // --font-weight-medium
    lineHeight: 1.5,
  },
  input: {
    fontSize: 14, // --text-base
    fontWeight: '400', // --font-weight-normal
    lineHeight: 1.5,
  },
  caption: {
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 1.5,
  },
};

// Spacing
export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
};

// Border radius (from Figma --radius: 0.625rem)
export const borderRadius = {
  xs: 4,
  sm: 8,
  md: 10, // --radius: 0.625rem = 10px
  lg: 14, // --radius-lg
  xl: 20, // --radius-xl
  round: 9999,
};

// Shadows
export const shadows = {
  sm: {
    shadowColor: 'rgba(0, 0, 0, 0.05)',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 1,
    shadowRadius: 2,
    elevation: 1,
  },
  md: {
    shadowColor: 'rgba(0, 0, 0, 0.1)',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 1,
    shadowRadius: 4,
    elevation: 3,
  },
  lg: {
    shadowColor: 'rgba(0, 0, 0, 0.15)',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 6,
  },
};

// Timing for animations
export const timing = {
  short: 150,
  medium: 300,
  long: 500,
};

// Export all theme elements
export default {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  timing,
};
